import type { MemberRoom, MemberRoomDraft, MemberRoomPacket } from '../../types'
import {
    CHANNEL_ID_START,
    FCRESPONSE_ERROR,
    FCUCR_BASICS,
    FCUCR_FRIENDS,
    FCUCR_MODELS,
    FCUCR_PREMIUMS,
} from './mfcConstants'

export const MEMBER_ROOM_VIDEO = { LOUNGE: 0, OWNER_WEBCAM: 1 } as const

// a MYUCR reply that refuses the viewer's room, or null
export const memberRoomCreateError = (packet: {
    arg1?: number
    arg2?: number
    payload?: unknown
}) => {
    const { arg1, arg2, payload } = packet
    const { err, errmsg } = (
        payload && typeof payload === 'object' ? payload : {}
    ) as { err?: number; errmsg?: string }
    if (!err && !errmsg && (arg1 || arg2 !== FCRESPONSE_ERROR)) return null
    return { errmsg: typeof errmsg === 'string' ? errmsg : undefined }
}

export const isFriendsOnlyRoom = (room: Pick<MemberRoom, 'permissions'>) =>
    (room.permissions & FCUCR_FRIENDS) !== 0

export const roomAllowsBasics = (room: Pick<MemberRoom, 'permissions'>) =>
    (room.permissions & FCUCR_BASICS) !== 0

export const roomAllowsModels = (room: Pick<MemberRoom, 'permissions'>) =>
    (room.permissions & FCUCR_MODELS) !== 0

export const isOwnerWebcamRoom = (room?: Pick<MemberRoom, 'video'>) =>
    room?.video === MEMBER_ROOM_VIDEO.OWNER_WEBCAM

export const findMemberRoomByOwner = (
    rooms: Record<number, MemberRoom>,
    ownerId: number,
) => Object.values(rooms).find((room) => room.ownerId === ownerId)

const memberRoomPermissions = (draft: MemberRoomDraft) =>
    (draft.friendsOnly ? FCUCR_FRIENDS : FCUCR_PREMIUMS) +
    (draft.allowBasics ? FCUCR_BASICS : 0) +
    (draft.allowModels ? FCUCR_MODELS : 0)

const stripMessageTokens = (text: string) => {
    let next = text
    while (/\{\{|\}\}/.test(next)) next = next.replace(/\{\{+|\}\}+/g, '')
    return next
}

export const sanitizeMemberRoomText = (text = '') =>
    stripMessageTokens(
        String(text)
            .replace(/["<>]/g, '')
            .replace(/&[\w#]+;?/g, ''),
    ).trim()

const sanitizeReceivedRoomName = (name = '') =>
    sanitizeMemberRoomText(String(name).replace(/&\S+/, ''))

export const toMemberRoom = (
    channelId: number,
    obj: MemberRoomPacket,
): MemberRoom => ({
    id: channelId,
    ownerId: channelId - CHANNEL_ID_START,
    ownerName: sanitizeMemberRoomText(obj?.o?.name),
    name: sanitizeReceivedRoomName(obj?.name),
    description: sanitizeMemberRoomText(obj?.description),
    color: obj?.c?.color || '',
    font: obj?.c?.font || 0,
    options: obj?.c?.opt || 0,
    background: obj?.c?.bg || 0,
    permissions: obj?.perm || 0,
    video: obj?.video || 0,
    visitors: obj?.count || 0,
    since: obj?.since || 0,
})

export const toDraftMemberRoom = (
    draft: MemberRoomDraft,
    ownerId: number,
    ownerName: string,
): MemberRoom => ({
    id: ownerId + CHANNEL_ID_START,
    ownerId,
    ownerName,
    name: sanitizeMemberRoomText(draft.name),
    description: sanitizeMemberRoomText(draft.description),
    color: draft.color.replace('#', ''),
    font: 0,
    options: 0,
    background: 0,
    permissions: memberRoomPermissions(draft),
    video: draft.video,
    visitors: 0,
    since: Math.floor(Date.now() / 1000),
})

export const memberRoomCreatePayload = (draft: MemberRoomDraft) => {
    const permissions = memberRoomPermissions(draft)
    return {
        permissions,
        payload: JSON.stringify({
            perm: permissions,
            name: sanitizeMemberRoomText(draft.name),
            video: draft.video,
            description: sanitizeMemberRoomText(draft.description),
            c: {
                font: 0,
                opt: 0,
                bg: 0,
                color: draft.color.replace('#', ''),
            },
        }),
    }
}
